import { useState, useEffect } from 'react';
import axios from 'axios';
import { PhoneOff, Volume2, VolumeX, Pause, Play, PhoneForwarded } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function CallControls({ call, callStatus, onHangup, socket }) {
  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  useEffect(() => {
    let interval;
    if (callStatus === 'answered') {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [callStatus]);

  const handleMute = async () => {
    if (!call || callStatus !== 'answered') return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/calls/${call.id}/mute`,
        { muted: !muted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMuted(!muted);
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  const handleHold = async () => {
    if (!call || callStatus !== 'answered') return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/calls/${call.id}/hold`,
        { on_hold: !onHold },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOnHold(!onHold);
    } catch (error) {
      console.error('Error toggling hold:', error);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!call) return null;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={handleMute}
          disabled={callStatus !== 'answered'}
          className={`p-3 rounded-full transition-colors ${
            muted
              ? 'bg-red-100 text-red-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>

        <button
          onClick={handleHold}
          disabled={callStatus !== 'answered'}
          className={`p-3 rounded-full transition-colors ${
            onHold
              ? 'bg-yellow-100 text-yellow-600'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={onHold ? 'Resume' : 'Hold'}
        >
          {onHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
        </button>

        <button
          onClick={onHangup}
          className="bg-red-600 text-white p-3 rounded-full hover:bg-red-700 transition-colors"
          title="Hangup"
        >
          <PhoneOff className="w-5 h-5" />
        </button>
      </div>

      <div className="text-center space-y-1">
        <div className="text-sm font-semibold text-gray-700">
          {callStatus === 'answered' && formatDuration(callDuration)}
          {callStatus === 'ringing' && 'Ringing...'}
          {callStatus === 'initiated' && 'Connecting...'}
          {callStatus === 'ended' && 'Call Ended'}
        </div>
        <div className="text-xs text-gray-500">
          {callStatus === 'answered' && 'Call in progress'}
          {callStatus === 'ringing' && 'Waiting for answer'}
          {callStatus === 'initiated' && 'Establishing connection'}
        </div>
      </div>
    </div>
  );
}

